/**
 * Utilities for date conversion between form format and ISO 8601
 */

/**
 * Converts date and time from form format (dd/mm/yyyy + hh:mm) to ISO 8601
 * @param {string} dateStr - Date in dd/mm/yyyy format
 * @param {string} timeStr - Time in hh:mm format
 * @returns {string} Date in ISO 8601 format (2025-06-20T02:00:00.000Z)
 */
export const formatToISO = (dateStr, timeStr = '00:00') => {
  if (!dateStr) return ''

  if (dateStr.includes('T') && dateStr.includes('Z')) {
    return dateStr
  }

  const [year, month, day] = dateStr.split('-')

  if (!year || !month || !day || year.length !== 4) {
    return ''
  }

  const [hours, minutes] = timeStr.split(':')

  const isoDate = `${dateStr}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00.000Z`

  return isoDate
}

/**
 * Converts date from ISO 8601 format to form format (dd/mm/yyyy)
 * @param {string} isoDate - Date in ISO 8601 format
 * @returns {string} Date in dd/mm/yyyy format
 */
export const formatFromISO = (isoDate) => {
  if (!isoDate) return ''

  if (isoDate.includes('/')) {
    return isoDate
  }

  try {
    const date = new Date(isoDate)

    const day = date.getUTCDate().toString().padStart(2, '0')
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
    const year = date.getUTCFullYear()

    return `${day}/${month}/${year}`
  } catch {
    return ''
  }
}

/**
 * Extracts hours and minutes from an ISO 8601 date in hh:mm format
 * @param {string} isoDate - Date in ISO 8601 format
 * @returns {string} Time in hh:mm format
 */
export const extractTimeFromISO = (isoDate) => {
  if (!isoDate) return ''

  if (!isoDate.includes('T')) {
    return isoDate
  }

  try {
    const date = new Date(isoDate)
    const hours = date.getUTCHours().toString().padStart(2, '0')
    const minutes = date.getUTCMinutes().toString().padStart(2, '0')

    return `${hours}:${minutes}`
  } catch {
    return ''
  }
}

/**
 * Converts date from form format (dd/mm/yyyy) to YYYY-MM-DD format (without time)
 * @param {string} dateStr - Date in dd/mm/yyyy format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatToDateOnly = (dateStr) => {
  if (!dateStr) return ''

  if (/^\d{4}-\d{2}-\d{2}$/.exec(dateStr)) {
    return dateStr
  }

  if (dateStr.includes('T')) {
    return dateStr.split('T')[0]
  }

  const [day, month, year] = dateStr.split('/')

  if (!day || !month || !year || year?.length !== 4) {
    return ''
  }

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

/**
 * Converts date from YYYY-MM-DD format (HTML datepicker) to ISO 8601 with time
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {string} timeStr - Time in HH:mm format
 * @returns {string} Date in ISO 8601 format
 */
export const formatDatePickerToISO = (dateStr, timeStr = '00:00') => {
  if (!dateStr) return ''

  if (dateStr.includes('T') && dateStr.includes('Z')) {
    return dateStr
  }

  // If already in YYYY-MM-DD format, just add the time
  if (/^\d{4}-\d{2}-\d{2}$/.exec(dateStr)) {
    const [hours, minutes] = timeStr.split(':')
    return `${dateStr}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00.000Z`
  }

  return ''
}

/**
 * Converts ISO 8601 date to YYYY-MM-DD format (HTML datepicker)
 * @param {string} isoDate - Date in ISO 8601 format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatISOToDatePicker = (isoDate) => {
  if (!isoDate) return ''

  if (/^\d{4}-\d{2}-\d{2}$/.exec(isoDate)) {
    return isoDate
  }

  try {
    const date = new Date(isoDate)
    const year = date.getUTCFullYear()
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
    const day = date.getUTCDate().toString().padStart(2, '0')

    return `${year}-${month}-${day}`
  } catch {
    return ''
  }
}
